import React, { useState, useEffect, useRef } from 'react';
import { FileText, Image as ImageIcon, Lock, UploadCloud, RefreshCw, X, AlertCircle, HardHat } from 'lucide-react';
import { checkUsageLimit, incrementUsage } from './services/usageService';
import { processOCR } from './services/ocrService';
import { processPDF } from './services/pdfService';
import { ResultPanel } from './components/ResultPanel';

export const CivilIntelligenceEngine = () => {
  const [file, setFile] = useState(null);
  const [resultText, setResultText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMsg, setStatusMsg] = useState('');
  const [usageData, setUsageData] = useState({ count: 0, locked: false, max: 3 });
  
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fileInputRef = useRef(null);
  const MAX_FILE_SIZE_MB = 10;

  useEffect(() => {
    document.title = "Civil AI Document Assistant | Sovereign Nexus";
    setUsageData(checkUsageLimit());
  }, []);

  const handleFileSelect = (e) => {
    if (usageData.locked) return;
    const selectedFile = e.dataTransfer?.files[0] || e.target.files[0];
    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      showToast(`حجم فایل نباید بیشتر از ${MAX_FILE_SIZE_MB} مگابایت باشد.`);
      return;
    }

    const isValidImage = selectedFile.type.startsWith('image/');
    const isValidPDF = selectedFile.type === 'application/pdf';
    
    if (!isValidImage && !isValidPDF) {
      showToast('فقط فرمت‌های تصویری و PDF پشتیبانی می‌شوند.');
      return;
    }

    setFile(selectedFile);
    setResultText('');
    setStatusMsg('');
  };

  const handleExtract = async () => {
    if (!file || usageData.locked) return;
    setIsProcessing(true);
    setProgress(5);
    setResultText('');

    try {
      let extractedText = '';
      const onProgress = (prog, msg) => {
        setProgress(prog);
        setStatusMsg(msg);
      };

      if (file.type.startsWith('image/')) {
        extractedText = await processOCR(file, onProgress);
      } else if (file.type === 'application/pdf') {
        extractedText = await processPDF(file, onProgress);
      }
      
      setResultText(extractedText);
      setUsageData(incrementUsage());
    } catch (error) {
      console.error(error);
      setStatusMsg('خطا در تحلیل سند.');
      showToast(error.message || 'خطا در ارتباط با موتور پردازشی.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6 text-slate-200 font-sans select-none relative" dir="rtl">
      {toast && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 text-sm font-bold animate-fade-in-up bg-[#0F1423] border border-rose-500/30 text-rose-400">
          <AlertCircle className="w-5 h-5" />
          <span>{toast.message}</span>
        </div>
      )}

      <div className="bg-[#0F1423] border border-white/5 rounded-3xl p-6 shadow-xl flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h2 className="text-lg font-black text-white flex items-center gap-2">
            <HardHat className="w-5 h-5 text-sky-400" />
            دستیار هوشمند اسناد مهندسی
          </h2>
          <p className="text-xs text-slate-400 [body.light-theme_&]:text-slate-600 mt-1">استخراج داده‌ها از فرم‌ها، گزارشات و نقشه‌های عمرانی.</p>
        </div>
        
        {!usageData.locked ? (
          <div className="flex items-center gap-2 text-xs font-medium px-3 py-1.5 bg-[#090D16] border border-white/5 rounded-xl text-slate-300">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            استفاده رایگان: {usageData.count} از {usageData.max}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs font-bold px-3 py-1.5 bg-sky-500/10 border border-sky-500/20 rounded-xl text-sky-400">
            <Lock className="w-3.5 h-3.5" />
            سهمیه رایگان امروز به پایان رسید
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="relative group">
            <input type="file" accept="image/jpeg, image/png, application/pdf" onChange={handleFileSelect} disabled={usageData.locked || isProcessing} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed z-10" ref={fileInputRef} />
            <div className={`h-40 border border-dashed rounded-3xl flex flex-col items-center justify-center transition-all duration-300 ${usageData.locked ? 'bg-[#090D16] border-white/5 opacity-60' : 'bg-[#0F1423] border-white/10 group-hover:border-sky-400/40 group-hover:bg-[#121829]'}`}>
              <UploadCloud className={`w-8 h-8 mb-3 ${usageData.locked ? 'text-slate-400 [body.light-theme_&]:text-slate-600 [body.light-theme_&]:text-slate-500' : 'text-sky-400 group-hover:scale-110 transition-transform'}`} />
              <p className="text-sm font-bold text-white mb-1">فنداسیون، نقشه یا گزارش را رها کنید</p>
              <p className="text-xs text-slate-500">پشتیبانی از JPG, PNG, PDF (حداکثر ۱۰ مگابایت)</p>
            </div>
          </div>

          {usageData.locked && (
            <div className="bg-gradient-to-br from-[#0F1423] to-[#151B2B] border border-sky-500/30 rounded-3xl p-6 text-center shadow-[0_0_20px_rgba(212,175,55,0.05)]">
              <div className="w-12 h-12 bg-sky-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4 text-sky-400">
                <Lock className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-white mb-2">نیاز به حساب کاربری</h3>
              <p className="text-xs text-slate-400 [body.light-theme_&]:text-slate-600 leading-relaxed mb-6">برای پردازش نامحدود و دسترسی به موتور هوشمند تحلیل گزارشات کارگاهی، به زودی حساب کاربری ایجاد کنید.</p>
            </div>
          )}

          {file && !usageData.locked && (
            <div className="bg-[#0F1423] border border-white/5 rounded-3xl p-4 flex flex-col gap-4">
              <div className="flex items-center justify-between border-b border-white/5 pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#090D16] rounded-lg">
                    {file.type.startsWith('image/') ? <ImageIcon className="w-4 h-4 text-slate-400 [body.light-theme_&]:text-slate-600" /> : <FileText className="w-4 h-4 text-slate-400 [body.light-theme_&]:text-slate-600" />}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white max-w-[200px] truncate">{file.name}</p>
                    <p className="text-[10px] text-slate-500 font-mono">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                <button onClick={() => { setFile(null); setResultText(''); setStatusMsg(''); }} className="text-slate-500 hover:text-rose-400 p-1 cursor-pointer transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <button onClick={handleExtract} disabled={isProcessing} className="w-full py-3 bg-sky-400 hover:bg-sky-500 text-[#090D16] font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer shadow-lg shadow-sky-400/10">
                {isProcessing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin text-[#090D16]" />
                    <span>{progress}% - در حال تحلیل...</span>
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 text-[#090D16]" />
                    <span>استخراج داده‌های سند</span>
                  </>
                )}
              </button>
              
              {statusMsg && <p className="text-[10px] text-center text-slate-500 font-mono">{statusMsg}</p>}
            </div>
          )}
        </div>

        <ResultPanel resultText={resultText} />
      </div>
    </div>
  );
};
